import "signalr";
import { EventEmitter } from "events";
import {
  VisitorCreateResult,
  VisitorInfo,
  ExpressionRequestModel,
  VisitorCreateRequest
} from "../types";
import VisitorService from "../services/VisitorService";
import StoreContainer from "../stores/StoreContainer";
import cookies from "../helpers/cookies";
import CookieStore from "./CookieStore";
import ParentWindowEventStore from "./ParentWindowEventStore";
import isMobile from "../helpers/isMobile";

class VisitorStore {
  private _isLoading = false;
  private _isLoaded = false;
  private _emitter = new EventEmitter();
  private _visitorId: string;
  private _details: VisitorCreateResult;

  private _pingRef: any;
  private readonly _pingInterval: number = 5 * 1000 * 60;
  private readonly _pingRetryInterval: number = 1000 * 60;
  private _cookieStore: CookieStore;
  private _parentWindowEventStore: ParentWindowEventStore;
  private _lastPing = 0;

  constructor(
    cookieStore: CookieStore,
    parentWindowEventStore: ParentWindowEventStore
  ) {
    this._cookieStore = cookieStore;
    this._parentWindowEventStore = parentWindowEventStore;
    this._visitorId = this._cookieStore.get("visitorId");
  }

  async load() {
    this._emitter.emit("loading");
    this._isLoading = true;
    this._isLoaded = false;
    const endOfDay = this._cookieStore.get("endOfDay");
    const hubspotId = cookies.get("hubspotutk");
    const siteId = StoreContainer.SettingStore.getSiteId();

    const request: VisitorCreateRequest = {
      VisitorID: this._visitorId,
      VisitorName: this.getVisitorName(),
      VisitorEmail: this.getVisitorEmail(),
      Actions: StoreContainer.VisitorActionStore.getActions(),
      CurrentUrl: window.parent.document.URL,
      DynamicProperties: StoreContainer.SettingStore.getCustomVars(),
      FirstVisit: this._cookieStore.get("firstvisit") || new Date(),
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      HasCookieSupport: true,
      HasVisitedToday: (endOfDay && new Date(endOfDay) > new Date()) || false,
      LastAgentChattedWith: "",
      Referrer: window.parent.document.referrer,
      ScreenSize: "",
      TimezoneOffset: "0",
      VisitCount: "0",
      IgnoreInvites: true,
      IgnoreGroupSelection: true,
      HubspotId: hubspotId,
      FromPopout: StoreContainer.FrameStore.isPopOut(),
      EngagementId: StoreContainer.EngagementStore.getEngagementId(),
      UtmCodes: StoreContainer.UtmStore.getCodes(),
      FrameVersion: 2
    };

    this._details = StoreContainer.SettingStore.isVisitorMonitoringDisabled()
      ? this.getDefaultVisitorDetails(request)
      : await VisitorService.getOrCreate(siteId, request);

    const savedEmail = StoreContainer.LocalStorageStore.get("visitorEmail");
    this._details.EmailAddress = this._details.EmailAddress || savedEmail;

    //If we failed to create a visitor due to a bad group, try fixing the problem
    //by clearing the engagement groupId cookie.
    //We have a problem where we'll attempt to load visitor / settings / config
    //all for the active groupId on page load without confirming that the group still exists.
    //Chat would then get stuck and the bad cookie would never clear.
    //The visitor would never be able to chat again without this check here.
    if (!this._details.Success && this._details.ErrorMessage === "bad group") {
      await StoreContainer.EngagementStore.setEngagementGroupId(undefined);
    }

    const groupSelectResult = await VisitorService.groupSelection(siteId, {
      VisitorId: this._details.VisitorID,
      GroupId: StoreContainer.GroupStore.getPageGroupId(),
      DisplayName: this.getVisitorName(),
      City: this._details.City,
      ISP: this._details.ISP,
      Region: this._details.Region,
      PostalCode: this._details.PostalCode,
      PageHitCount: StoreContainer.PageHitStore.getCount(),
      WasInviteFired: StoreContainer.InviteStore.wasInviteFired(),
      HasChatted: StoreContainer.VisitorActionStore.hasChatted(),
      CustomVars: StoreContainer.SettingStore.getCustomVars(),
      InviteViews: StoreContainer.InviteStore.getInviteViews(),
      PreviousPages: StoreContainer.PageHitStore.getPreviousPages(),
      CurrentUrl: this._details.CurrentPage || "",
      Host: window.parent.location.host,
      Referrer: window.parent.document.referrer,
      Country: this._details.Country,
      UtmCodes: StoreContainer.UtmStore.getCodes()
    });

    if (groupSelectResult) {
      StoreContainer.GroupStore.changePageGroupId(groupSelectResult.NewGroupId);

      if (
        groupSelectResult.GroupChanged &&
        !StoreContainer.GroupStore.hasActiveGroup()
      ) {
        this._details.GroupID = groupSelectResult.NewGroupId;

        console.log(
          `%c Group changed from ${groupSelectResult.OldGroupId} to ${groupSelectResult.NewGroupId} by rule ${groupSelectResult.ChangeSyntax}`,
          "background: #00388d; color: #ffffff"
        );
      }
    }

    this._cookieStore.set("endOfDay", this._details.EndOfDay);
    this._cookieStore.set("firstvisit", this._details.FirstVisit);

    VisitorService.postSession(
      siteId,
      this._details.VisitorID,
      this._details.GroupID
    );

    this._visitorId = this._details.VisitorID;

    const googleAnalyticsId = cookies.get("_ga");
    if (
      googleAnalyticsId &&
      !this._details.GoogleAnalyticsId &&
      this._visitorId
    ) {
      VisitorService.sendGoogleAnalyticsId(
        siteId,
        this._visitorId,
        googleAnalyticsId
      );
    }

    if (hubspotId && !this._details.HubspotId && this._visitorId) {
      VisitorService.sendHubspotId(siteId, this._visitorId, hubspotId);
    }

    this._cookieStore.set("visitorId", this._visitorId);

    this._isLoading = false;
    this._isLoaded = true;
    this._emitter.emit("loaded");

    this.ping();

    this._parentWindowEventStore.addEventListener(
      "focus",
      this.handleWindowFocus
    );
  }

  unbindEvents() {
    this._parentWindowEventStore.removeEventListener(
      "focus",
      this.handleWindowFocus
    );

    clearTimeout(this._pingRef);
  }

  handleWindowFocus = () => {
    console.log("Focus regained. Restarting ping.");
    clearTimeout(this._pingRef);
    this.ping();
  };

  onLoading(callback: () => void) {
    this._emitter.addListener("loading", callback);
  }

  afterLoaded(callback: () => void) {
    if (this.isLoaded()) {
      callback();
    } else {
      this.onLoaded(callback);
    }
  }

  onLoaded(callback: () => void) {
    this._emitter.addListener("loaded", callback);
  }

  removeAllListeners() {
    this._emitter.removeAllListeners();
  }

  isLoading() {
    return this._isLoading;
  }

  isLoaded() {
    return this._isLoaded;
  }

  getVisitorId() {
    return this._visitorId;
  }

  hasVisitorId() {
    return !!this._visitorId;
  }

  setVisitorId(id: string) {
    this._visitorId = id;
  }

  setVisitorName(name: string) {
    StoreContainer.LocalStorageStore.set("visitorDisplayName", name);
    this._details.DisplayName = name;
  }

  getVisitorName() {
    return (
      this._details?.DisplayName ||
      StoreContainer.LocalStorageStore.get("visitorDisplayName")
    );
  }

  setVisitorEmail(email: string) {
    StoreContainer.LocalStorageStore.set("visitorEmail", email);
    this._details.EmailAddress = email;
  }

  setVisitorPhone(phone: string) {
    StoreContainer.LocalStorageStore.set("visitorPhone", phone);
    this._details.PhoneNumber = phone;
  }

  getVisitorEmail() {
    return (
      this._details?.EmailAddress ||
      StoreContainer.LocalStorageStore.get("visitorEmail")
    );
  }

  getVisitorPhone() {
    return (
      this._details?.PhoneNumber ||
      StoreContainer.LocalStorageStore.get("visitorPhone")
    );
  }

  getExpressionRequestModel(): ExpressionRequestModel {
    return {
      GroupId: this._details.GroupID,
      DisplayName: this.getVisitorName(),
      City: this._details.City,
      ISP: this._details.ISP,
      Region: this._details.Region,
      PostalCode: this._details.PostalCode,
      PageHitCount: StoreContainer.PageHitStore.getCount(),
      WasInviteFired: StoreContainer.InviteStore.wasInviteFired(),
      HasChatted: StoreContainer.VisitorActionStore.hasChatted(),
      CustomVars: StoreContainer.SettingStore.getCustomVars(),
      InviteViews: StoreContainer.InviteStore.getInviteViews(),
      PreviousPages: StoreContainer.PageHitStore.getPreviousPages(),
      CurrentUrl: window.parent.location.href,
      Host: window.parent.location.host,
      Referrer: window.parent.document.referrer,
      Country: this._details.Country,
      VisitorId: this._details.VisitorID,
      UtmCodes: StoreContainer.UtmStore.getCodes()
    };
  }

  async runInvites() {
    const invites = await VisitorService.getInvites(
      StoreContainer.SettingStore.getSiteId(),
      this.getExpressionRequestModel()
    );

    if (!invites || !invites.length) {
      return;
    }

    const timeOnPageInvites = invites.filter(function (invite) {
      return invite.TimeOnPageRule;
    });

    timeOnPageInvites.forEach(function (invite) {
      StoreContainer.InviteStore.handleRecievedTimedInvite(
        invite.TimeOnPageRule,
        invite.InviteId
      );
    });

    const normalInvites = invites.filter(function (invite) {
      return !invite.TimeOnPageRule;
    });

    if (normalInvites && normalInvites.length) {
      StoreContainer.InviteStore.handleReceivedInvite(
        normalInvites[0].InviteId
      );
    }
  }

  getVisitorInfo(): VisitorInfo {
    return {
      AreaCode: this._details.AreaCode,
      MostRecentPageHit: this._details.MostRecentPageHit,
      City: this._details.City,
      Country: this._details.Country,
      DisplayName: this.getVisitorName(),
      DomainName: this._details.DomainName,
      EmailAddress: this.getVisitorEmail(),
      FirstVisit: new Date(this._details.FirstVisit),
      HeadshotUrl: this._details.HeadshotUrl,
      IPAddress: this._details.IPAddress,
      ISP: this._details.ISP,
      IntegrationConnections: this._details.IntegrationConnections,
      Keywords: this._details.Keywords,
      Language: this._details.Language,
      Latitude: this._details.Latitude,
      Location: this._details.Location,
      Longitude: this._details.Longitude,
      Platform: this._details.Platform,
      PostalCode: this._details.PostalCode,
      ReferredBy: this._details.ReferredBy,
      VisitorID: this._visitorId,
      CountryCode: this._details.CountryCode,
      BrowserName: this._details.BrowserName,
      BrowserVersion: this._details.BrowserVersion,
      CustomVars: StoreContainer.SettingStore.getCustomVars(),
      Actions: StoreContainer.VisitorActionStore.getActions(),
      UtmCodes: StoreContainer.UtmStore.getCodes(),
      IsMobile: isMobile()
    };
  }

  private shouldPing() {
    const delta = Date.now() - this._lastPing;
    return delta > 1000 * 30;
  }

  private async ping() {
    if (!this._visitorId) {
      return;
    }

    if (!this.shouldPing()) {
      return;
    }

    this._lastPing = Date.now();

    await VisitorService.ping(
      StoreContainer.SettingStore.getSiteId(),
      this._visitorId
    ).then(
      () => {
        this.onPingSuccess();
      },
      () => {
        this.onPingFail();
      }
    );
  }

  private onPingSuccess() {
    this._pingRef = setTimeout(() => {
      this.ping();
    }, this._pingInterval);
  }

  private onPingFail() {
    this._pingRef = setTimeout(() => {
      this.ping();
    }, this._pingRetryInterval);
  }

  private getDefaultVisitorDetails(
    request: VisitorCreateRequest
  ): VisitorCreateResult {
    return {
      ReferredBy: request.Referrer,
      Platform: undefined,
      Longitude: undefined,
      Location: undefined,
      Latitude: undefined,
      Language: "en",
      Keywords: undefined,
      IntegrationConnections: undefined,
      IPAddress: undefined,
      HeadshotUrl: undefined,
      EmailAddress: undefined,
      DomainName: undefined,
      Country: undefined,
      CountryCode: undefined,
      MostRecentPageHit: undefined,
      AreaCode: undefined,
      VisitorID: this._visitorId,
      GroupID: request.GroupID,
      DisplayName: undefined,
      City: undefined,
      ISP: undefined,
      Region: undefined,
      PostalCode: undefined,
      CurrentPage: undefined,
      FirstVisit: request.FirstVisit,
      VisitCount: request.VisitCount,
      BrowserName: undefined,
      BrowserVersion: undefined,
      EndOfDay: undefined,
      Invites: undefined,
      PreRouteResult: request.PreRouteResult,
      GoogleAnalyticsId: undefined,
      HubspotId: request.HubspotId,
      Success: true,
      ErrorMessage: undefined,
      PhoneNumber: undefined
    };
  }
}

export default VisitorStore;
